import type { LegacyPaymentOption, PaymentOption } from '@niarab2c/frontend-commons/src/types/hotels';
import React from 'react';
import type { HotelPostSearchRule } from 'niara-spear-crudmodel';
import { CreditCardConditions } from '../../HotelDetails/PaymentConditions/PaymentConditions';
import Accordion, { Panel } from '../Accordion';
import Price from '../Price';
import Points from '../Points';
import * as S from './styles';
export type MoreValueOptionsProps = {
  paymentOptions: (LegacyPaymentOption | PaymentOption)[];
  priceDisplayMode?: HotelPostSearchRule['priceDisplayMode'];
};
export const icons = {
  DIR: 'htlExtDirectPaymentAtTheHotelOutlined',
  INT_DIR: 'htlIntDirectPaymentAtTheHotelOutlined',
  DIR_IATA: 'sysLanguage',
  OFF_CC: 'htlCreditCardsOutlined',
  ON_CC: 'htlCreditCardsOutlined',
  ON_PIX: 'htlPixOutlined',
  OFF_ANT: 'htlBankDepositOutlined',
  OFF_FAT: 'htlInvoicedPaymentOutlined',
  LATER: 'sysPaymentAfterOutlined'
} as const;
const MoreValueOptions: React.FC<MoreValueOptionsProps> = ({
  paymentOptions,
  priceDisplayMode = 'TOTAL'
}) => {
  const sortedPaymentOptions = paymentOptions?.filter(paymentOption => paymentOption?.enabled || paymentOption?.enabled == null).sort((a, b) => {
    const aActualTotalValue = 'priceComposition' in a ? a?.priceComposition?.total?.value : 'actualTotalValue' in a ? a?.actualTotalValue : 0;
    const bActualTotalValue = 'priceComposition' in b ? b?.priceComposition?.total?.value : 'actualTotalValue' in b ? b?.actualTotalValue : 0;
    return aActualTotalValue - bActualTotalValue;
  }).sort((a, b) => (a?.enabled || a?.enabled == null ? 0 : 1) - (b?.enabled || b?.enabled == null ? 0 : 1));
  const minActualTotalValue = sortedPaymentOptions?.reduce((min, option) => {
    const actualTotalValue = 'priceComposition' in option ? option?.priceComposition?.total?.value : 'actualTotalValue' in option ? option?.actualTotalValue : null;
    if (actualTotalValue && (min === undefined || actualTotalValue < min)) {
      return actualTotalValue;
    }
    return min;
  }, undefined as number | undefined);
  const allActualTotalValuesAreEqual = minActualTotalValue !== undefined && sortedPaymentOptions?.every(option => 'actualTotalValue' in option && option.actualTotalValue === minActualTotalValue);
  return <>
      <Accordion initialActive={[0]}>
        {sortedPaymentOptions?.map((paymentOption, idx) => {
        const theBestActualTotalValue = minActualTotalValue !== undefined && !allActualTotalValuesAreEqual && 'actualTotalValue' in paymentOption && paymentOption.actualTotalValue === minActualTotalValue;
        const nights = 'priceComposition' in paymentOption ? paymentOption?.priceComposition?.nights?.length : 1;
        const value = 'priceComposition' in paymentOption && paymentOption?.priceComposition?.total?.value ? paymentOption?.priceComposition?.total?.value : 'paymentValue' in paymentOption ? paymentOption?.paymentValue : null;
        const nightValue = value ? value / nights : null;
        const currency = 'priceComposition' in paymentOption && paymentOption?.priceComposition?.total?.currency ? paymentOption?.priceComposition?.total?.currency : 'paymentCurrency' in paymentOption ? paymentOption?.paymentCurrency : null;
        const pointsToBurn = 'pointsToBurn' in paymentOption ? paymentOption?.pointsToBurn : null;
        const pointsValue = 'paymentValue' in paymentOption ? paymentOption?.paymentValue ?? 0 : 0;
        const nightPointsToBurn = 'pointsToBurn' in paymentOption ? paymentOption?.pointsToBurn / nights : null;
        const nightPointsValue = 'paymentValue' in paymentOption ? (paymentOption?.paymentValue ?? 0) / nights : 0;
        return <Panel key={idx} niaraIcon={icons[paymentOption?.type]} id={idx} sizeTitle={13} paddingHeaderY={20} header={<S.Header>
                  <S.ContentPayment>
                    <S.PaymentText>{paymentOption?.alias} </S.PaymentText>
                    {theBestActualTotalValue && <S.BestValue>Melhor Valor</S.BestValue>}
                  </S.ContentPayment>
                  {value > 0 && <S.ContentValue>
                      <S.Value colorGreen={theBestActualTotalValue}>
                        {priceDisplayMode === 'PER_NIGHT' && <>
                            <span>Noite:</span>
                            {nightPointsToBurn > 0 ? <>
                                <Price value={nightPointsValue} fontSizeAll={16} fontWeightAll="800" currency={currency} />{' '}
                                +{' '}
                                <Points value={nightPointsToBurn} pointsSize={13} fractionSize={13} fontWeightAll="800" unitSize={13} unitText={'loyaltyRedeemUnitText' in paymentOption ? paymentOption?.loyaltyRedeemUnitText : "pontos"} />
                              </> : <Price value={nightValue} fontSizeAll={16} fontWeightAll="800" currency={currency} />}
                          </>}
                        {priceDisplayMode === 'TOTAL' && <>
                            <span>Total:</span>
                            {pointsToBurn > 0 ? <>
                                <Price value={pointsValue} fontSizeAll={16} fontWeightAll="800" currency={currency} /> +{' '}
                                <Points value={pointsToBurn} pointsSize={13} fractionSize={13} fontWeightAll="800" unitSize={13} unitText={'loyaltyRedeemUnitText' in paymentOption ? paymentOption?.loyaltyRedeemUnitText : "pontos"} />
                              </> : <Price value={value} fontSizeAll={16} fontWeightAll="800" currency={currency} />}
                          </>}
                      </S.Value>
                      {priceDisplayMode === 'PER_NIGHT' && <S.Value colorGreen={theBestActualTotalValue} fontSize={12} fontWeight="500">
                          <span>Total:</span>
                          {pointsToBurn > 0 ? <>
                              <Price value={pointsValue} fontSizeAll={12} fontWeightAll="500" currency={currency} /> +{' '}
                              <Points value={pointsToBurn} pointsSize={11} fractionSize={11} fontWeightAll="500" unitSize={11} unitText={'loyaltyRedeemUnitText' in paymentOption ? paymentOption?.loyaltyRedeemUnitText : "pontos"} />
                            </> : <Price value={value} fontSizeAll={12} fontWeightAll="800" currency={currency} />}
                        </S.Value>}
                    </S.ContentValue>}
                </S.Header>} childrenPaddingX={20}>
              <S.Description>
                {paymentOption?.description}
                <CreditCardConditions paymentOption={paymentOption} />
              </S.Description>
            </Panel>;
      })}
      </Accordion>
    </>;
};
export default MoreValueOptions;